import React, { useContext } from 'react';
import classnames from 'classnames';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import Modal, { MODAL_FOOTER_BUTTON_SIZE } from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import { useEffectOnce } from '@wix/da-hooks/pkg/useEffectOnce';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import Sidebar from '../_partials/Sidebar';
import UploadButton from '../_partials/UploadButton';
import UploadDropzone from '../_partials/UploadDropzone';
import { MediaSourceType, MediaPopupMode } from '../types';
import { DEFAULT_MEDIASOURCE_TYPES_FOR_MEDIA_SELECTOR } from '../constants';
import MediaSourceMenu from '../_partials/MediaSourceMenu';
import GallectionExplorer from '../Gallections/GallectionExplorer';
import GallectionDeviationSelector from '../Deviations/DeviationSelector/GallectionDeviationSelector';
import BrowseDeviationSelector from '../Deviations/DeviationSelector/BrowseDeviationSelector';
import CommissionSelector from '../Commissions/CommissionSelector';
import PostDeviationSelector from '../Deviations/DeviationSelector/PostDeviationSelector';
import TierSelector from '../Tiers/TierSelector';
import TierExplorer from '../Tiers/TierExplorer';
import withOwnReduxStore from '../_hocs/withOwnReduxStore';
import withCurrentMediaSource from '../_hocs/withCurrentMediaSource';
import withCurrentGallection from '../_hocs/withCurrentGallection';
import withCurrentTier from '../_hocs/withCurrentTier';
import withSelectedDeviations from '../_hocs/withSelectedDeviations';
import withMediaPopupInit from '../_hocs/withMediaPopupInit';
import withSearch from '../_hocs/withSearch';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TierDeviationSelector from '../Deviations/DeviationSelector/TierDeviationSelector';
import mediaPopupStyles from '../MediaPopup.scss';
const enhance = compose(withOwnReduxStore, withCurrentMediaSource, withMediaPopupInit, withCurrentGallection, withCurrentTier, withSelectedDeviations, withSearch);
const MediaSelector = ({ initialMediaSource = MediaSourceType.GALLERY, mediaSourceTypes, withMediaSourceSelector = true, selectionLimit = 1, initMediaPopup, allowedDeviationTypes, allowedDeviationResourceTypes, allowGifs = true, allowPremium = false, allowInTierContent = true, allowMature = true, allowUploads = true, allowMultipleUploads = true, ignoreDeviationIds, minDeviationWidth, minDeviationHeight, gruser, currentMediaSource, currentGallection, currentTier, searchQuery, className, contentClassName, onSelect, onClose, submitBtnLabel, selectedDeviations, title, subheading })=>{
    const { t } = useTranslation();
    const isMobile = useContext(MobileContext);
    const onSelectHandler = ()=>onSelect && onSelect(selectedDeviations);
    useEffectOnce(()=>{
        initMediaPopup({
            mode: MediaPopupMode.SELECTOR,
            mediaSource: initialMediaSource,
            mediaSourceTypes: mediaSourceTypes !== null && mediaSourceTypes !== void 0 ? mediaSourceTypes : DEFAULT_MEDIASOURCE_TYPES_FOR_MEDIA_SELECTOR,
            withMediaSourceSelector,
            selectionLimit,
            allowedDeviationTypes,
            allowedDeviationResourceTypes,
            allowGifs,
            allowPremium,
            allowInTierContent,
            allowMature,
            allowUploads,
            allowMultipleUploads,
            ignoreDeviationIds,
            minDeviationWidth,
            minDeviationHeight,
            gruser
        });
    });
    const getTitle = ()=>{
        if (title) {
            return title;
        }
        return selectionLimit && selectionLimit > 1 ? t('mediapopup.selectDeviations') : t('mediapopup.selectDeviation');
    };
    const renderSidebar = ()=>{
        if (!withMediaSourceSelector) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(Sidebar, null, allowUploads && /*#__PURE__*/ React.createElement(UploadButton, {
            className: mediaPopupStyles['upload-btn']
        }), /*#__PURE__*/ React.createElement(MediaSourceMenu, {
            mediaSourceTypes: mediaSourceTypes
        }));
    };
    const renderContentArea = ()=>{
        switch(currentMediaSource){
            case MediaSourceType.BROWSE:
                return /*#__PURE__*/ React.createElement(BrowseDeviationSelector, null);
            case MediaSourceType.STATUS:
            case MediaSourceType.POLL:
            case MediaSourceType.JOURNAL:
            case MediaSourceType.POSTS:
                return /*#__PURE__*/ React.createElement(PostDeviationSelector, null);
            case MediaSourceType.TIER:
                return /*#__PURE__*/ React.createElement(TierSelector, null);
            case MediaSourceType.TIER_DEVIATIONS:
            case MediaSourceType.TIER_POSTS:
                if (!currentTier) {
                    return /*#__PURE__*/ React.createElement(TierExplorer, null);
                }
                return /*#__PURE__*/ React.createElement(TierDeviationSelector, null);
            case MediaSourceType.COMMISSIONS:
                return /*#__PURE__*/ React.createElement(CommissionSelector, null);
            default:
                if (!currentGallection && !searchQuery) {
                    return /*#__PURE__*/ React.createElement(GallectionExplorer, null);
                }
                return /*#__PURE__*/ React.createElement(GallectionDeviationSelector, null);
        }
    };
    return /*#__PURE__*/ React.createElement(Modal, {
        isOpen: true,
        heading: getTitle(),
        subheading: subheading,
        showHeaderBorder: true,
        showFooterBorder: true,
        className: classnames(mediaPopupStyles['root'], className),
        contentClassName: classnames(mediaPopupStyles['content'], contentClassName),
        onRequestClose: onClose,
        footerRight: [
            /*#__PURE__*/ React.createElement(BrandedButton, {
                key: "submit",
                size: MODAL_FOOTER_BUTTON_SIZE,
                variant: "brand-green",
                onClick: onSelectHandler,
                disabled: !selectedDeviations.length
            }, submitBtnLabel || t('mediapopup.select'))
        ]
    }, !isMobile && allowUploads && /*#__PURE__*/ React.createElement(UploadDropzone, null), !isMobile && renderSidebar(), renderContentArea());
};
MediaSelector.displayName = 'MediaSelector';
export default enhance(MediaSelector);
